import { IMAGES } from '@/lib/constants/images';
import { DEMO_ROUTE, PRICING_ROUTE } from '@/lib/constants/routes';
import {
    faArrowRight,
    faChartLine,
    faCrosshairs,
    faPeopleGroup,
    faRocket,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, CardBody } from '@heroui/react';
import { InstagramEmbed } from 'react-social-media-embed';
import KeyFeature from './KeyFeature';
import { Link } from '@tanstack/react-router';

function LoggedOut() {
    return (
        <div className=' grid  grid-flow-row '>
            <div className='mx-auto mt-2 grid h-min gap-y-3  md:max-w-7xl '>
                <div className='gap-2 justify-self-center px-12 py-10 text-center'>
                    <h1 className='bg-linear-to-r mb-4 from-green-500 to-teal-500 bg-clip-text pb-2 text-5xl font-bold tracking-tight text-transparent'>
                        The Ultimate Feedback Tool For Better Golf
                    </h1>
                    <div className='mt-3 text-2xl font-semibold text-gray-700'>
                        Caddy Stats provides the best insights into your golf game, to help you unlock your
                        full potential
                    </div>
                </div>
                <div className='mb-8 flex flex-row items-center gap-4 justify-self-center'>
                    <Button
                        color='primary'
                        size='lg'
                        href={DEMO_ROUTE}
                        as='a'
                        className='bg-linear-to-tr from-green-500 to-teal-500 font-semibold'>
                        View Demo
                    </Button>
                    <Button
                        variant='bordered'
                        size='lg'
                        className='font-semibold'
                        as='a'
                        href={PRICING_ROUTE}>
                        See Pricing
                    </Button>
                </div>
            </div>

            <div className='h-full w-full bg-gray-50 pb-16'>
                <div className='mx-auto mt-2 grid h-min gap-y-3  md:max-w-7xl '>
                    <div className=' grid items-center gap-2 px-2  md:px-24'>
                        <div className='gap-2 justify-self-center px-12 py-10 text-center'>
                            <div className='mt-3 text-4xl font-semibold '>Key Features</div>
                        </div>
                        <div className='grid items-stretch gap-3 md:grid-cols-3'>
                            <KeyFeature
                                title='Complete Statistical Analysis'
                                points={[
                                    'Complete strokes gained analysis across every shot you take',
                                    'Historical tracking to see progression in each aspect of your game over time',
                                    'Traditional statistics like GIR% and driving accuracy, along with our own Big Five metrics',
                                ]}
                                image={IMAGES.caddy_reading_green}
                                imageAlt='caddy and player reading green'
                                icon={faChartLine}
                            />
                            <KeyFeature
                                title='Decision Making'
                                points={[
                                    'Harness immediate feedback after recording your round to help guide your next practice session',
                                    'Understand your shot patterns and improve your make percentage on putts for better scoring',
                                    'Gain confidence with detailed performance data',
                                ]}
                                image={IMAGES.fairway}
                                imageAlt='beautiful golf fairway'
                                icon={faCrosshairs}
                            />
                            <KeyFeature
                                title='Built For Teams & Coaches'
                                points={[
                                    'Quickly compare players performance side-by-side',
                                    "Easily identify trends in a player' game to guide practice needs",
                                    'Filter on different rounds to measure performance depending on temperature & wind',
                                ]}
                                image={IMAGES.caddy_walking}
                                imageAlt='caddy walking with golfer'
                                icon={faPeopleGroup}
                            />
                        </div>
                    </div>
                    <div className='flex items-center justify-center bg-gray-50 p-4'>
                        <Card
                            isHoverable
                            className='bg-linear-to-r max-w-md border-none from-green-500 to-teal-500 p-[2px]'>
                            <Card className='bg-white dark:bg-gray-900'>
                                <CardBody className='gap-5 p-8 text-center'>
                                    <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
                                        <FontAwesomeIcon icon={faRocket} className='h-8 w-8 text-green-500' />
                                    </div>

                                    <div className='space-y-3'>
                                        <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100'>
                                            Take Your Golf Game to the Next Level
                                        </h2>
                                        <p className='text-gray-500 dark:text-gray-400'>
                                            Get started tracking your stats with our premium features! Join
                                            the group of golfers that truly want to get better
                                        </p>
                                    </div>

                                    <div className='space-y-3'>
                                        <Button
                                            size='lg'
                                            color='primary'
                                            className='w-full font-semibold'
                                            as={Link}
                                            href={PRICING_ROUTE}
                                            endContent={<FontAwesomeIcon icon={faArrowRight} />}>
                                            Get Started Now
                                        </Button>
                                        <p className='text-sm text-gray-400'>30 day free trial!</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Card>
                    </div>
                    <div className='mt-6 grid items-center gap-2  px-2 md:px-24'>
                        <Card className='overflow-hidden' isHoverable>
                            <CardBody className='p-0'>
                                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                                    <div className='h-[240px] md:h-full'>
                                        <img
                                            src={IMAGES.cade_swing}
                                            alt={'title'}
                                            className='h-full w-full object-cover'
                                        />
                                    </div>
                                    <div className='flex flex-col justify-between p-6'>
                                        <div className='space-y-4'>
                                            <h2 className='text-2xl font-semibold'>
                                                What is Strokes Gained?
                                            </h2>
                                            <div className='flex flex-col gap-2 text-default-500'>
                                                <div>Want to know how the pros measure performance?</div>
                                                <div>
                                                    Strokes gained is a revolutionary statistic that breaks
                                                    down every shot to reveal your strengths and weaknesses on
                                                    the course.
                                                </div>
                                                <div>
                                                    Whether you're a beginner or a seasoned golfer,
                                                    understanding this metric can transform your strategy and
                                                    lower your scores. Ready to dive in?
                                                </div>
                                            </div>
                                        </div>
                                        <div className='mt-6'>
                                            <Button
                                                endContent={<FontAwesomeIcon icon={faArrowRight} />}
                                                color='primary'
                                                as={Link}
                                                to='/blog/what-is-strokes-gained'
                                                variant='flat'
                                                className='text-black'>
                                                Read More
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className='gap-2 justify-self-center px-12 py-10 text-center'>
                        <div className='mt-3 text-4xl font-semibold '>User Spotlights</div>
                    </div>
                    <div className='mx-auto'>
                        <Card>
                            {/* <CardHeader className='text-2xl font-semibold'>
                                Find us on instagram
                                <SocialIcon
                                    url='https://www.instagram.com/caddystatsgolf/'
                                    bgColor={colors.green[500]}
                                    className='ml-2 h-4'
                                />
                            </CardHeader> */}
                            <CardBody>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <InstagramEmbed
                                        url='https://www.instagram.com/p/DFpyiDjJhHa/?img_index=1'
                                        captioned
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    {/* <div className='mx-auto flex flex-col md:flex-row'>
                        <Card isBlurred>
                            <CardBody>
                                <div className='flex  flex-col-reverse gap-4 md:flex-row'>
                                    <div className='mx-auto aspect-auto h-24 w-auto rounded-lg md:h-48 md:w-auto '>
                                        <img
                                            src={IMAGES.scorecard}
                                            alt='golf scorecard'
                                            className='mx-auto aspect-auto h-24 w-auto rounded-lg md:h-48 md:w-auto '
                                        />
                                    </div>
                                    <div className='flex flex-col justify-between'>
                                        <div className='text-2xl font-semibold'>Our Mission</div>
                                        <div>
                                            We are dedicated to providing a comprehensive platform for golfers
                                            and coaches to analyze and improve their game.
                                        </div>
                                        <div className='mt-2 list-inside'>
                                            Our innovative software allows users to:
                                            {[
                                                'Track performance across every part of their game',
                                                'Quickly identify strengths and weaknesses',
                                                'Elevate their skills on the course',
                                            ]?.map((feature, index) => (
                                                <div className='flex items-center gap-2' key={index}>
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className='text-green-500'
                                                    />
                                                    <div>{feature}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className=' my-6 grid items-center  gap-2 px-2 md:mx-auto md:w-1/2'>
                        <Card>
                            <CardBody>
                                <div className='flex  flex-col-reverse items-center gap-4 md:flex-row'>
                                    <img
                                        src={IMAGES.cade_swing}
                                        alt='golf swing silhouette'
                                        className='mx-auto aspect-auto h-24 w-auto rounded-lg md:h-48 md:w-auto '
                                    />
                                    <div>
                                        Join us in the pursuit of excellence and unleash your full potential
                                        on the course. Let Caddy Stats be your trusted guide into the journey
                                        towards playing better golf.
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div> */}
                </div>
            </div>
        </div>
    );
}

export default LoggedOut;
